import { useRef } from 'react';

// Keep track of loading items by key.
export const useLoadingManager = onLoadingChanged => {
  const loadingItems = useRef({});
  const lastIsLoading = useRef(true);

  const updateIsLoading = () => {
    const newIsLoading = !isFinishedLoading();

    if (newIsLoading !== lastIsLoading.current) {
      onLoadingChanged?.(newIsLoading);
      lastIsLoading.current = newIsLoading;
    }
  };

  const isFinishedLoading = () => Object.values(loadingItems.current).every(Boolean);

  const beginLoading = item => {
    loadingItems.current[item] = false;

    updateIsLoading();
  };

  // Similar to beginLoading, but if the item already exists we don't change the value.
  const beginLoadingIfMissing = item => {
    if (!Object.prototype.hasOwnProperty.call(loadingItems.current, item)) {
      loadingItems.current[item] = false;
    }

    updateIsLoading();
  };

  const isLoading = item => !loadingItems.current[item];

  const finishedLoading = item => {
    loadingItems.current[item] = true;

    updateIsLoading();
  };

  const reset = () => {
    loadingItems.current = {};

    updateIsLoading();
  };

  const resetExcept = exceptionItems => {
    const newLoadingItems = {};

    for (const item of exceptionItems) {
      newLoadingItems[item] = loadingItems.current[item];
    }

    loadingItems.current = newLoadingItems;

    updateIsLoading();
  };

  const stillLoading = () => Object.values(loadingItems.current).filter(v => !v);

  return {
    isFinishedLoading,
    beginLoading,
    beginLoadingIfMissing,
    finishedLoading,
    isLoading,
    reset,
    resetExcept,
    stillLoading,
  };
};
